// Chat.tsx
import { useState, useEffect, useRef, useCallback } from "react"; // Import useCallback
import { usePresence } from "./presence-context";
import React from "react";
import CursorPreview from "./CursorPreview";
import DomainVerification from "./DomainVerification";

// Export the chat active state for use in other components
export let isChatActive = false;
export const setIsChatActiveGlobal = (value: boolean) => {
  isChatActive = value;
};

// NEW EXPORT: Function to get the current state of isEnterTimeoutActive
let _isEnterTimeoutActiveRef: React.MutableRefObject<boolean> | null = null;
export const setEnterTimeoutRef = (ref: React.MutableRefObject<boolean>) => {
  _isEnterTimeoutActiveRef = ref;
};
export const getIsEnterTimeoutActive = (): boolean => {
  return _isEnterTimeoutActiveRef ? _isEnterTimeoutActiveRef.current : false;
};

// NEW EXPORT: Function to clear the enter timeout from external components
let _clearEnterTimeoutRef: (() => void) | null = null;
export const setClearEnterTimeoutRef = (clearFn: () => void) => {
  _clearEnterTimeoutRef = clearFn;
};
export const clearEnterTimeoutExternal = () => {
  if (_clearEnterTimeoutRef) {
    _clearEnterTimeoutRef();
  }
};

export default function Chat() {
  const [listening, setListening] = useState(false);
  const [message, setMessage] = useState<string>("");
  
  const [verifiedDomain, setVerifiedDomain] = useState<string | null>(null);
  // New state to track if the verification modal is open
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false); 
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const isEnterTimeoutActive = useRef(false); 

  const { updatePresence, showCTA, otherUsers } = usePresence((state) => {
    const otherUsers = Array.from(state.otherUsers.values());
    let showCTA = false;
    for (const user of otherUsers) {
      if (user.presence?.message) {
        showCTA = true;
        break;
      }
    }

    return {
      updatePresence: state.updatePresence,
      showCTA,
      otherUsers: state.otherUsers,
    };
  });

  // Function to clear enter timeout - made it a useCallback so it can be passed as ref
  const clearEnterTimeout = useCallback(() => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
      isEnterTimeoutActive.current = false;
    }
  }, []);

  // Set up the external clear function reference
  useEffect(() => {
    setClearEnterTimeoutRef(clearEnterTimeout);
    return () => {
      _clearEnterTimeoutRef = null;
    };
  }, [clearEnterTimeout]);

  const getAvailableCursorIndices = () => {
    const totalCursors = 10;
    const usedIndices = new Set<number>();
    
    otherUsers.forEach(user => {
      if (user.presence.cursorIndex !== undefined) {
        usedIndices.add(user.presence.cursorIndex);
      }
    });

    const availableIndices = [];
    for (let i = 0; i < totalCursors; i++) {
      if (!usedIndices.has(i)) {
        availableIndices.push(i);
      }
    }
    return availableIndices;
  };

  // Made switchToNextCursor a useCallback to be passed to CursorPreview
  const switchToNextCursor = useCallback(() => {
    const currentIndex = parseInt(localStorage.getItem("cursorIndex") || "0");
    const availableIndices = getAvailableCursorIndices();
    
    if (availableIndices.length === 0) {
      return;
    }

    let nextIndex = currentIndex;
    const currentIndexInAvailable = availableIndices.indexOf(currentIndex);
    
    if (currentIndexInAvailable === -1) {
      nextIndex = availableIndices[0];
    } else {
      const nextAvailableIndex = (currentIndexInAvailable + 1) % availableIndices.length;
      nextIndex = availableIndices[nextAvailableIndex];
    }

    localStorage.setItem("cursorIndex", String(nextIndex));
    
    const storedVerifiedDomain = localStorage.getItem('verifiedDomain');
    updatePresence({ 
      cursorIndex: nextIndex,
      verifiedDomain: storedVerifiedDomain
    });

    // Clear the enter timeout when switching cursor so user can type again
    clearEnterTimeout();
    
  }, [getAvailableCursorIndices, updatePresence, clearEnterTimeout]); // Added clearEnterTimeout to dependencies

  const handleVerificationComplete = (domain: string, publicKey: string) => {
    setVerifiedDomain(domain);
    localStorage.setItem('verifiedDomain', domain);
    localStorage.setItem('verifiedDomainKey', publicKey);
    updatePresence({ verifiedDomain: domain });
    console.log(`Domain ${domain} verified successfully!`);
  };

  // Callback for DomainVerification to update modal state
  const handleVerificationModalStateChange = useCallback((isOpen: boolean) => {
    setIsVerificationModalOpen(isOpen);
  }, []);

  useEffect(() => {
    const storedDomain = localStorage.getItem('verifiedDomain');
    if (storedDomain) {
      setVerifiedDomain(storedDomain);
      updatePresence({ verifiedDomain: storedDomain });
    }
  }, [updatePresence]);

  useEffect(() => {
    const active = listening || message.length > 0;
    setIsChatActiveGlobal(active);
    
    // Expose to window so vanilla JS scripts (like room.js) can read the chat state
    (window as any).isCursorChatActive = active;
  }, [listening, message]);

  // Modified effect for the 7-second timeout
  useEffect(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // Only set timeout if listening AND modal is NOT open
    if (listening && !isVerificationModalOpen) { 
      typingTimeoutRef.current = setTimeout(() => {
        setListening(false);
        setMessage("");
      }, 7000);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [message, listening, isVerificationModalOpen]); // Add isVerificationModalOpen to dependencies

  // Instantly clear the draft if the user opens the verification modal
  useEffect(() => {
    if (isVerificationModalOpen && listening) {
      setMessage("");
    }
  }, [isVerificationModalOpen, listening]);

  useEffect(() => {
    return () => {
      clearEnterTimeout();
    };
  }, [clearEnterTimeout]);

  const isInputElement = (element: Element | null): boolean => {
    if (!element) return false;
    
    const tagName = element.tagName;
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
      return true;
    }
    
    if (element instanceof HTMLElement) {
      return element.contentEditable === 'true';
    }
    
    return false;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if we're in the verification modal - if so, don't intercept any keys
      if (isVerificationModalOpen) {
        return;
      }

      if (event.key === "Escape") {
        setListening(false);
        setMessage("");
        
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        clearEnterTimeout();
       
        return;
      }

      if (!listening) {
        if (event.key === "/") {
          const activeElement = document.activeElement;
          const isInputFocused = isInputElement(activeElement);
          
          if (!isInputFocused) {
            event.preventDefault();
            setMessage("");
            setListening(true);
            // Clear enter timeout when starting to type again
            clearEnterTimeout();
          }
        }
      } else {
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          if (event.key === "Enter") {
            setListening(false);
            
            clearEnterTimeout(); // Clear any existing timeout before setting new one
            isEnterTimeoutActive.current = true;
            enterTimeoutRef.current = setTimeout(() => {
              setMessage("");
              isEnterTimeoutActive.current = false;
            }, 30000);
          } else if (event.key === "Backspace") {
            setMessage((prev) => prev.slice(0, -1));
          } else if (event.key.length === 1) {
            setMessage((prev) => {
              return prev.length < 64 ? prev + event.key : prev;
            });
          }

          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [listening, otherUsers, updatePresence, isVerificationModalOpen, clearEnterTimeout]); // Added clearEnterTimeout to dependencies

  useEffect(() => {
    const storedVerifiedDomain = localStorage.getItem('verifiedDomain');
    updatePresence({ 
      message: message.length > 0 ? message : null,
      verifiedDomain: storedVerifiedDomain
    });
  }, [message, updatePresence]);

  // Set the global ref on mount
  useEffect(() => {
    setEnterTimeoutRef(isEnterTimeoutActive);
    return () => {
      _isEnterTimeoutActiveRef = null; // Clean up on unmount
    };
  }, []);

  return (
    <>
      <DomainVerification 
        onVerificationComplete={handleVerificationComplete} 
        isChatListening={listening} 
        onModalStateChange={handleVerificationModalStateChange} // Pass the new callback
      /> 
      {/* Pass switchToNextCursor to CursorPreview */}
      <CursorPreview onSwitchCursor={switchToNextCursor} />
    </>
  );
}